import React from "react";
import useFormattingUtils from "../../../../../../context/useFormattingUtils";
import { getDefaultInvoiceColumns } from "./invoiceLayoutDefaults";
import { amountInWords } from "./numberToWords";

const FONT_SIZES = { small: "12px", medium: "14px", large: "16px" };
const FONT_FAMILIES = {
  default: "Arial, Helvetica, sans-serif",
  serif: "Georgia, 'Times New Roman', serif",
  modern: "'Segoe UI', 'Poppins', system-ui, sans-serif",
  monospace: "'Courier New', monospace",
};

// Wraps a piece of template content so it can be edited in place from the
// Template Editor's "advanced" mode: hovering shows a pencil icon, clicking
// calls back to the settings page with which field to edit. Outside the
// editor (real printing, checkout previews, the design gallery) `onEdit` is
// not passed and this renders as a completely inert span/div.
const Editable = ({ as: Tag = "div", editing, onEdit, className = "", children, ...rest }) => {
  if (!editing || !onEdit) {
    const Plain = Tag;
    return <Plain className={className} {...rest}>{children}</Plain>;
  }
  return (
    <Tag
      className={`${className} a4-editable`}
      onClick={(e) => {
        e.stopPropagation();
        onEdit();
      }}
      {...rest}
    >
      {children}
      <span className="a4-edit-pencil" aria-hidden="true">&#9998;</span>
    </Tag>
  );
};

// Settings-driven full A4 printable invoice.
// props: layout (per-invoice-type settings object), invoice (normalized invoice),
//        storeInfo (store name/address/logo/contact from context),
//        editing (bool, advanced Template Editor mode is on),
//        onEditField (fieldKey) => void -- called when an editable region is clicked
const A4InvoiceTemplate = ({ layout = {}, invoice = {}, storeInfo = {}, editing = false, onEditField }) => {
  const { formatCurrency, formatDate } = useFormattingUtils();
  const fontSize = FONT_SIZES[layout.font_size] || FONT_SIZES.medium;
  const fontFamily = FONT_FAMILIES[layout.font_family] || FONT_FAMILIES.default;
  const brandColor = layout.brand_color || "#0056B3";
  const items = invoice.items || [];
  const totals = invoice.totals || {};
  const columns = { ...getDefaultInvoiceColumns(), ...(layout.columns || {}) };
  const colStyle = (key) => (columns[key]?.width ? { width: `${columns[key].width}px` } : undefined);
  const showDiscountCol = layout.show_item_discount && columns.discount?.visible !== false;
  const showTaxCol = layout.show_tax_breakdown && columns.tax?.visible !== false;
  const showQtyCol = columns.qty?.visible !== false;
  const showRateCol = columns.rate?.visible !== false;
  const showAmountCol = columns.amount?.visible !== false;
  // Business identity: an explicit override in the layout settings always
  // wins over whatever the Store record has (which may be blank or stale).
  const businessName = layout.business_name || storeInfo.name || "Store Name";
  const businessAddress = layout.business_address || storeInfo.address || "";
  const businessContact = layout.business_contact || storeInfo.contact || "";
  // Always follow the store's configured currency/date format rather than a
  // hardcoded symbol or raw date string, matching every other invoice screen.
  const money = (amount) => formatCurrency(amount || 0);

  const design = ["classic", "modern", "banded", "compact"].includes(layout.design) ? layout.design : "classic";

  const editField = (key) => (editing && onEditField ? () => onEditField(key) : undefined);

  return (
    <div className={`a4-invoice-wrap design-${design}`} style={{ fontSize }}>
      <style>{`
        .a4-invoice-wrap { --brand: ${brandColor}; --brand-tint: color-mix(in srgb, ${brandColor} 15%, white); width: 100%; max-width: 800px; margin: 0 auto; background: #fff; color: #222; padding: 24px; font-family: ${fontFamily}; }
        .a4-invoice-wrap .a4-header-image { width: 100%; max-height: 140px; object-fit: contain; margin-bottom: 14px; display: block; }
        .a4-invoice-wrap .a4-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid var(--brand); padding-bottom: 10px; margin-bottom: 16px; }
        .a4-invoice-wrap .a4-header img.a4-logo { height: 44px; object-fit: contain; }
        .a4-invoice-wrap .a4-store-name { font-size: 1.4em; font-weight: 800; color: var(--brand); margin: 0; }
        .a4-invoice-wrap .a4-meta { text-align: right; font-size: 0.9em; }
        .a4-invoice-wrap .a4-block { margin-bottom: 14px; }
        .a4-invoice-wrap table { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
        .a4-invoice-wrap th, .a4-invoice-wrap td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; font-size: 0.95em; }
        .a4-invoice-wrap th { background: var(--brand-tint); color: var(--brand); }
        .a4-invoice-wrap .a4-totals { margin-left: auto; width: 280px; }
        .a4-invoice-wrap .a4-totals-row { display: flex; justify-content: space-between; padding: 3px 0; }
        .a4-invoice-wrap .a4-totals-row.grand { font-weight: bold; border-top: 2px solid var(--brand); margin-top: 4px; padding-top: 6px; }
        .a4-invoice-wrap .a4-amount-words { font-size: 0.85em; font-style: italic; color: #444; margin: 10px 0; }
        .a4-invoice-wrap .a4-bank-details { font-size: 0.85em; margin-bottom: 14px; border: 1px solid #eee; border-radius: 6px; padding: 10px 12px; }
        .a4-invoice-wrap .a4-bank-details strong { display: block; margin-bottom: 4px; color: var(--brand); }
        .a4-invoice-wrap .a4-terms { font-size: 0.8em; color: #555; margin-bottom: 14px; white-space: pre-wrap; }
        .a4-invoice-wrap .a4-terms strong { display: block; color: #333; margin-bottom: 4px; }
        .a4-invoice-wrap .a4-footer { text-align: center; margin-top: 24px; font-size: 0.85em; color: #555; border-top: 1px solid #ddd; padding-top: 10px; }
        .a4-invoice-wrap .a4-footer-image { display: block; max-width: 100%; max-height: 120px; margin: 18px auto 0; object-fit: contain; }
        .a4-invoice-wrap .a4-signature { margin-top: 60px; display: flex; justify-content: flex-end; }
        .a4-invoice-wrap .a4-signature .line { width: 200px; border-top: 1px solid #333; text-align: center; padding-top: 4px; font-size: 0.85em; }

        /* --- Advanced Template Editor: click-to-edit affordance --- */
        .a4-invoice-wrap .a4-editable { position: relative; cursor: pointer; border-radius: 4px; transition: background-color 0.1s; }
        .a4-invoice-wrap .a4-editable:hover { background-color: rgba(0, 86, 179, 0.08); outline: 1px dashed rgba(0, 86, 179, 0.4); }
        .a4-invoice-wrap .a4-edit-pencil { position: absolute; top: -8px; right: -8px; width: 20px; height: 20px; border-radius: 50%; background: var(--brand); color: #fff; font-size: 11px; display: flex; align-items: center; justify-content: center; opacity: 0; transition: opacity 0.1s; box-shadow: 0 1px 4px rgba(0,0,0,0.3); }
        .a4-invoice-wrap .a4-editable:hover .a4-edit-pencil { opacity: 1; }

        /* --- Design: Modern -- minimal lines, no boxed table, airy spacing --- */
        .a4-invoice-wrap.design-modern { padding: 32px; }
        .a4-invoice-wrap.design-modern .a4-header { border-bottom: 1px solid #e2e2e2; align-items: flex-start; }
        .a4-invoice-wrap.design-modern .a4-store-name { color: #1a1a1a; letter-spacing: 0.02em; }
        .a4-invoice-wrap.design-modern .a4-meta { color: var(--brand); font-weight: 600; }
        .a4-invoice-wrap.design-modern table { margin-bottom: 20px; }
        .a4-invoice-wrap.design-modern th, .a4-invoice-wrap.design-modern td { border: none; border-bottom: 1px solid #eee; padding: 8px 6px; }
        .a4-invoice-wrap.design-modern th { background: transparent; color: #888; font-weight: 600; text-transform: uppercase; font-size: 0.78em; letter-spacing: 0.04em; border-bottom: 2px solid #1a1a1a; }
        .a4-invoice-wrap.design-modern .a4-totals-row.grand { border-top: 1px solid #1a1a1a; }
        .a4-invoice-wrap.design-modern .a4-footer { border-top: none; }

        /* --- Design: Banded -- brand-color band behind the header --- */
        .a4-invoice-wrap.design-banded .a4-header { background: var(--brand); color: #fff; border-bottom: none; padding: 18px 20px; border-radius: 8px; margin-bottom: 20px; }
        .a4-invoice-wrap.design-banded .a4-store-name { color: #fff; }
        .a4-invoice-wrap.design-banded .a4-meta { color: #fff; opacity: 0.92; }
        .a4-invoice-wrap.design-banded th { background: var(--brand); color: #fff; }
        .a4-invoice-wrap.design-banded .a4-totals-row.grand { background: var(--brand-tint); border-top: none; border-radius: 4px; padding: 8px 10px; margin-top: 8px; }

        /* --- Design: Compact -- dense spacing for shorter invoices --- */
        .a4-invoice-wrap.design-compact { padding: 16px; }
        .a4-invoice-wrap.design-compact .a4-header { padding-bottom: 6px; margin-bottom: 10px; }
        .a4-invoice-wrap.design-compact .a4-store-name { font-size: 1.15em; }
        .a4-invoice-wrap.design-compact .a4-block { margin-bottom: 8px; }
        .a4-invoice-wrap.design-compact th, .a4-invoice-wrap.design-compact td { padding: 3px 6px; font-size: 0.85em; }
        .a4-invoice-wrap.design-compact table { margin-bottom: 8px; }
        .a4-invoice-wrap.design-compact .a4-totals-row { padding: 1px 0; font-size: 0.9em; }
        .a4-invoice-wrap.design-compact .a4-signature { margin-top: 30px; }
        .a4-invoice-wrap.design-compact .a4-footer { margin-top: 12px; padding-top: 6px; }

        @media print {
          @page { size: A4; margin: 12mm; }
          body * { visibility: hidden; }
          /* Scope visibility to the dedicated print mount only. Other
             on-screen previews (e.g. the invoice modal's own live preview)
             also render an .a4-invoice-wrap, and a bare class selector would
             reveal every one of them at once -- producing a duplicated
             invoice and a spurious extra page. */
          .__invoice_print_root, .__invoice_print_root * { visibility: visible; }
          .__invoice_print_root .a4-invoice-wrap { position: absolute; left: 0; top: 0; width: 100%; }
          .a4-edit-pencil { display: none !important; }
        }
      `}</style>

      {layout.header_image && <img src={layout.header_image} alt="" className="a4-header-image" />}

      <div className="a4-header">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {layout.show_logo && storeInfo.logo && <img src={storeInfo.logo} alt="logo" className="a4-logo" />}
          <Editable as="h1" className="a4-store-name" editing={editing} onEdit={editField("business_name")}>
            {businessName}
          </Editable>
        </div>
        <div className="a4-meta">
          <div><strong>Invoice #:</strong> {invoice.invoiceNo}</div>
          <div><strong>Date:</strong> {formatDate(invoice.date) || invoice.date}</div>
        </div>
      </div>

      {layout.show_store_address && (businessAddress || businessContact || editing) && (
        <Editable className="a4-block" editing={editing} onEdit={editField("business_address")}>
          <div>{businessAddress || (editing ? "Click to set business address" : "")}</div>
          <div>{businessContact}</div>
        </Editable>
      )}

      {(layout.header_note || editing) && (
        <Editable className="a4-block" editing={editing} onEdit={editField("header_note")}>
          <em>{layout.header_note || (editing ? "Click to set a header note" : "")}</em>
        </Editable>
      )}

      {layout.show_customer_block && (
        <div className="a4-block">
          <strong>Bill To:</strong>
          <div>{invoice.customer?.name}</div>
          <div>{invoice.customer?.address}</div>
          <div>{invoice.customer?.phone}</div>
        </div>
      )}

      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Item</th>
            {showQtyCol && <th style={colStyle("qty")}>Qty</th>}
            {showRateCol && <th style={colStyle("rate")}>Rate</th>}
            {showDiscountCol && <th style={colStyle("discount")}>Discount</th>}
            {showTaxCol && <th style={colStyle("tax")}>Tax</th>}
            {showAmountCol && <th style={colStyle("amount")}>Amount</th>}
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => (
            <tr key={idx}>
              <td>{idx + 1}</td>
              <td>{item.name}</td>
              {showQtyCol && <td style={colStyle("qty")}>{item.qty}</td>}
              {showRateCol && <td style={colStyle("rate")}>{money(item.rate)}</td>}
              {showDiscountCol && <td style={colStyle("discount")}>{money(item.discount)}</td>}
              {showTaxCol && <td style={colStyle("tax")}>{money(item.tax)}</td>}
              {showAmountCol && <td style={colStyle("amount")}>{money(item.amount)}</td>}
            </tr>
          ))}
        </tbody>
      </table>

      {(layout.show_amount_in_words || editing) && (
        <Editable
          as="div"
          className="a4-amount-words"
          editing={editing}
          onEdit={editField("show_amount_in_words")}
        >
          Amount in words: {amountInWords(totals.payableAmount, invoice.currencyCode)}
          {editing && !layout.show_amount_in_words ? " (currently hidden -- click to toggle)" : ""}
        </Editable>
      )}

      <div className="a4-totals">
        <div className="a4-totals-row"><span>Total Qty</span><span>{totals.totalQty}</span></div>
        <div className="a4-totals-row"><span>Sub Total</span><span>{money(totals.subTotal)}</span></div>
        {layout.show_tax_breakdown && (
          <div className="a4-totals-row"><span>Tax</span><span>{money(totals.taxAmount)}</span></div>
        )}
        <div className="a4-totals-row"><span>Discount</span><span>{money(totals.discountAmount)}</span></div>
        <div className="a4-totals-row"><span>Round Off</span><span>{money(totals.roundOff)}</span></div>
        <div className="a4-totals-row grand"><span>Payable</span><span>{money(totals.payableAmount)}</span></div>
        <div className="a4-totals-row"><span>Payment</span><span>{totals.payMethod}</span></div>
      </div>

      {(layout.show_bank_details || editing) && (
        <Editable className="a4-bank-details" editing={editing} onEdit={editField("bank_details")}>
          <strong>Bank Details</strong>
          <div>{layout.bank_name || (editing ? "Click to set bank details" : "")}</div>
          {layout.bank_account_number && <div>A/C: {layout.bank_account_number}</div>}
          {layout.bank_ifsc && <div>IFSC: {layout.bank_ifsc}</div>}
          {layout.bank_branch && <div>Branch: {layout.bank_branch}</div>}
        </Editable>
      )}

      {layout.show_signature_line && (
        <div className="a4-signature">
          <div className="line">Authorized Signature</div>
        </div>
      )}

      {(layout.show_terms || editing) && (
        <Editable className="a4-terms" editing={editing} onEdit={editField("terms_text")}>
          <strong>Terms &amp; Conditions</strong>
          {layout.terms_text || (editing ? "Click to set terms & conditions" : "")}
        </Editable>
      )}

      {(layout.show_notes_footer || editing) && (
        <Editable
          as="div"
          className="a4-footer"
          editing={editing}
          onEdit={editField("footer_note")}
        >
          {layout.footer_note || (editing ? "Click to set a footer note" : "")}
        </Editable>
      )}

      {layout.footer_image && <img src={layout.footer_image} alt="" className="a4-footer-image" />}
    </div>
  );
};

export default A4InvoiceTemplate;
